import { useCallback, useEffect, useRef, useState } from 'react'

import { audienceImportSessionsApi } from '@/lib/api'
import {
  onImportSessionUpdated,
  subscribeToImportSession,
  unsubscribeFromImportSession,
} from '@/lib/services/websocket'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import { incrementPollWonRepair } from '@/lib/realtime/pollWonMetrics'
import { getApiErrorMessage } from '@/lib/get-api-error-message'

import type { AudienceCreationFlowSnapshot } from '@/types/components/audiences/import-session'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'

const PHASE1_CAP = 49
const PHASE2_CAP = 99
const TICK_MS = 3_000

type Phase = 'idle' | 'uploading' | 'preflight' | 'done'

interface UsePreflightProgressOptions {
  onComplete: (snapshot: AudienceCreationFlowSnapshot) => void
  onError: (message: string) => void
}

export interface UsePreflightProgressReturn {
  progress: number | null
  startPhase1: () => void
  enterPhase2: (sessionId: string, snapshot: AudienceCreationFlowSnapshot) => void
  reset: () => void
}

function isPreflightFailure(
  status: AudienceCreationFlowSnapshot['preflightStatus'],
): boolean {
  return status === 'rejected' || status === 'failed'
}

function isPreflightComplete(
  snapshot: AudienceCreationFlowSnapshot,
): boolean {
  return (
    snapshot.preflightStatus === 'completed' ||
    snapshot.hasPreflightData === true
  )
}

/**
 * Stay on the upload progress UI until Python preflight has a real outcome.
 *
 * `status !== 'pending'` used to treat `null` as done, so a 201 that had not
 * yet stamped `pending` (or `hasPreflightData: false`) navigated away immediately.
 * The following GET /flow was then aborted on unmount, which Chrome reports as CORS.
 */
function shouldWaitForDatasetPreflight(
  snapshot: AudienceCreationFlowSnapshot,
): boolean {
  if (isPreflightFailure(snapshot.preflightStatus)) {
    return false
  }
  if (isPreflightComplete(snapshot)) {
    return false
  }
  return (
    snapshot.preflightStatus === 'pending' ||
    snapshot.hasPreflightData === false
  )
}

export function usePreflightProgress(
  options: UsePreflightProgressOptions,
): UsePreflightProgressReturn {
  const [progress, setProgress] = useState<number | null>(null)
  const [repairSessionId, setRepairSessionId] = useState<string | null>(null)

  const phaseRef = useRef<Phase>('idle')
  const runTokenRef = useRef(0)
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const wsCleanupRef = useRef<(() => void) | null>(null)
  const subscribedSessionRef = useRef<string | null>(null)
  const resolvedRef = useRef(false)
  const earlyEventRef = useRef<ImportSessionUpdatedEvent | null>(null)

  const optionsRef = useRef(options)
  optionsRef.current = options

  const clearTick = useCallback(() => {
    if (tickIntervalRef.current !== null) {
      clearInterval(tickIntervalRef.current)
      tickIntervalRef.current = null
    }
  }, [])

  const clearPoll = useCallback(() => {
    setRepairSessionId(null)
  }, [])

  const clearWs = useCallback(() => {
    wsCleanupRef.current?.()
    wsCleanupRef.current = null
    if (subscribedSessionRef.current) {
      unsubscribeFromImportSession(subscribedSessionRef.current)
      subscribedSessionRef.current = null
    }
  }, [])

  const cleanupAll = useCallback(() => {
    clearTick()
    clearPoll()
    clearWs()
  }, [clearTick, clearPoll, clearWs])

  const bumpRunToken = useCallback(() => {
    runTokenRef.current += 1
  }, [])

  const reset = useCallback(() => {
    cleanupAll()
    bumpRunToken()
    phaseRef.current = 'idle'
    resolvedRef.current = false
    earlyEventRef.current = null
    setProgress(null)
  }, [bumpRunToken, cleanupAll])

  const resolve = useCallback(
    async (sessionId: string) => {
      const token = runTokenRef.current
      if (resolvedRef.current) return
      resolvedRef.current = true
      cleanupAll()
      phaseRef.current = 'done'
      setProgress(100)
      try {
        const finalSnapshot =
          await audienceImportSessionsApi.getFlowSnapshot(sessionId)
        if (token !== runTokenRef.current) return
        optionsRef.current.onComplete(finalSnapshot)
      } catch (err) {
        if (token !== runTokenRef.current) return
        optionsRef.current.onError(
          getApiErrorMessage(err, 'Could not load the import session.'),
        )
        setProgress(null)
        phaseRef.current = 'idle'
        resolvedRef.current = false
      }
    },
    [cleanupAll],
  )

  const handlePreflightError = useCallback(
    (snapshot: AudienceCreationFlowSnapshot) => {
      const token = runTokenRef.current
      cleanupAll()
      phaseRef.current = 'idle'
      resolvedRef.current = false
      setProgress(null)
      if (token !== runTokenRef.current) return
      const msg =
        snapshot.preflightError?.message ??
        (snapshot.preflightStatus === 'rejected'
          ? "The uploaded file doesn't contain usable rows for demographic analysis."
          : 'We hit an unexpected error while analyzing your dataset.')
      optionsRef.current.onError(msg)
    },
    [cleanupAll],
  )

  const startPhase1 = useCallback(() => {
    cleanupAll()
    bumpRunToken()
    phaseRef.current = 'uploading'
    resolvedRef.current = false
    earlyEventRef.current = null
    setProgress(0)

    tickIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev === null || phaseRef.current !== 'uploading') return prev
        return Math.min(prev + 1, PHASE1_CAP)
      })
    }, TICK_MS)
  }, [bumpRunToken, cleanupAll])

  const enterPhase2 = useCallback(
    (sessionId: string, snapshot: AudienceCreationFlowSnapshot) => {
      const token = runTokenRef.current
      const isStale = () => token !== runTokenRef.current

      clearTick()

      if (isPreflightFailure(snapshot.preflightStatus)) {
        if (isStale()) return
        handlePreflightError(snapshot)
        return
      }

      if (!shouldWaitForDatasetPreflight(snapshot)) {
        if (isStale()) return
        cleanupAll()
        phaseRef.current = 'done'
        resolvedRef.current = true
        setProgress(100)
        optionsRef.current.onComplete(snapshot)
        return
      }

      const early = earlyEventRef.current
      if (
        early &&
        (early.reason === 'preflight_completed' ||
          early.reason === 'preflight_rejected' ||
          early.reason === 'preflight_failed')
      ) {
        earlyEventRef.current = null
        if (isStale()) return
        if (early.reason === 'preflight_completed') {
          void resolve(sessionId)
        } else {
          handlePreflightError(snapshot)
        }
        return
      }

      phaseRef.current = 'preflight'
      setRepairSessionId(sessionId)
      setProgress((prev) => Math.max(prev ?? 0, 20))

      tickIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev === null || phaseRef.current !== 'preflight') return prev
          return Math.min(prev + 1, PHASE2_CAP)
        })
      }, TICK_MS)

      const handleWsEvent = (event: ImportSessionUpdatedEvent) => {
        if (isStale()) return
        if (phaseRef.current === 'uploading') {
          earlyEventRef.current = event
          return
        }
        if (resolvedRef.current) return

        if (event.reason === 'preflight_completed') {
          void resolve(sessionId)
        } else if (
          event.reason === 'preflight_rejected' ||
          event.reason === 'preflight_failed'
        ) {
          audienceImportSessionsApi
            .getFlowSnapshot(sessionId)
            .then((s) => {
              if (isStale()) return
              handlePreflightError(s)
            })
            .catch(() => {
              if (isStale()) return
              handlePreflightError({
                ...snapshot,
                preflightStatus: event.reason === 'preflight_rejected' ? 'rejected' : 'failed',
              })
            })
        }
      }

      subscribeToImportSession(sessionId)
      subscribedSessionRef.current = sessionId
      wsCleanupRef.current = onImportSessionUpdated(sessionId, handleWsEvent)

      const applyFreshSnapshot = (
        fresh: AudienceCreationFlowSnapshot,
      ) => {
        if (isStale() || resolvedRef.current) return
        if (isPreflightFailure(fresh.preflightStatus)) {
          handlePreflightError(fresh)
          return
        }
        if (!shouldWaitForDatasetPreflight(fresh)) {
          void resolve(sessionId)
        }
      }

      void audienceImportSessionsApi
        .getFlowSnapshot(sessionId)
        .then(applyFreshSnapshot)
        .catch(() => {
          // Network / CORS-looking aborts: stall repair retries.
        })
    },
    [clearTick, cleanupAll, resolve, handlePreflightError],
  )

  useRealtimeRepair({
    active: repairSessionId !== null,
    onRepair: (reason) => {
      incrementPollWonRepair('IMPORT', reason)
      const sessionId = repairSessionId
      if (!sessionId || resolvedRef.current) {
        return
      }
      void audienceImportSessionsApi
        .getFlowSnapshot(sessionId)
        .then((fresh) => {
          if (resolvedRef.current) {
            return
          }
          if (isPreflightFailure(fresh.preflightStatus)) {
            handlePreflightError(fresh)
            return
          }
          if (!shouldWaitForDatasetPreflight(fresh)) {
            void resolve(sessionId)
          }
        })
        .catch(() => undefined)
    },
  })

  useEffect(() => {
    return () => {
      cleanupAll()
    }
  }, [cleanupAll])

  return { progress, startPhase1, enterPhase2, reset }
}
